/* eslint-disable react-hooks/immutability */
import { useCallback, useEffect, useRef } from "react";

export interface UseWebSocketOptions<T = any> {
  url: string;
  token: string;
  onMessage?: (data: T, event: MessageEvent<string>) => void;
  onOpen?: (event?: Event) => void;
  onClose?: (event?: CloseEvent) => void;
  onError?: (event?: Event) => void;
  onDisable?: () => void;
}

const isDisable = import.meta.env.VITE_APP_DISABLE_WSS === "true";

export function useWebSocket<T = any>(options: UseWebSocketOptions<T>) {
  const { url, token, onMessage, onOpen, onClose, onError, onDisable } =
    options;

  // Refs for callbacks to prevent re-connections on render
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  const onDisableRef = useRef(onDisable);

  // 1. NEW: Track if we closed the socket intentionally (to stop the reconnect loop)
  const isExplicitClose = useRef(false);

  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
    onDisableRef.current = onDisable;
  }, [onMessage, onOpen, onClose, onError, onDisable]);

  const socketRef = useRef<WebSocket | null>(null);
  const intervalRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const heartbeatTimeoutRef = useRef<number | null>(null);
  const lastPingTimeRef = useRef<number>(0);
  const retryCount = useRef(0);

  const MAX_RETRIES = 3;
  // const BASE_DELAY = 1000;
  // const MAX_DELAY = 30000;
  const HEARTBEAT_TIMEOUT = 30000;

  const cleanup = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (retryTimeoutRef.current !== null) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (heartbeatTimeoutRef.current !== null) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    // Basic validation
    if (!token || !url) {
      return;
    }
    if (isDisable) {
      if (onDisableRef.current) onDisableRef.current();
      return;
    }

    // 2. Prevent creating a new socket if one is already CONNECTING or OPEN
    if (
      socketRef.current?.readyState === WebSocket.OPEN ||
      socketRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // Reset explicit close flag for new connection
    isExplicitClose.current = false;

    // Clean up timers
    cleanup();

    const wsUrl = `${url}?token=${token}`;
    const randomKey = `tmatab_${Date.now()}`;
    window.sessionStorage.setItem("tabId", randomKey);

    console.log(`WebSocket connecting (attempt ${retryCount.current + 1})`);

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = (event: Event) => {
      console.log("WebSocket Connected successfully");
      retryCount.current = 0;
      if (onOpenRef.current) onOpenRef.current(event);

      // Start Heartbeat
      intervalRef.current = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          const pingTime = Date.now();
          lastPingTimeRef.current = pingTime;
          socket.send(
            JSON.stringify({
              action: "heartbeat",
              message: "PING",
              timestamp: pingTime,
            })
          );

          if (heartbeatTimeoutRef.current)
            clearTimeout(heartbeatTimeoutRef.current);

          heartbeatTimeoutRef.current = window.setTimeout(() => {
            console.warn("Heartbeat timeout - closing connection");
            socket.close(3001, "Heartbeat timeout");
          }, HEARTBEAT_TIMEOUT);
        }
      }, 1000 * 60 * 5);
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      try {
        const data: T = JSON.parse(event.data);
        if (
          data &&
          typeof data === "object" &&
          (("action" in data && (data as any).action === "heartbeat") ||
            ("response" in data && (data as any).response === "OK"))
        ) {
          if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current);
            heartbeatTimeoutRef.current = null;
          }
          return;
        }
        if (onMessageRef.current) onMessageRef.current(data, event);
      } catch (err) {
        console.error("Parse error", err);
      }
    };

    const scheduleReconnect = (reason: string, code?: number) => {
      // 3. STOP THE LOOP: If we closed this explicitly, DO NOT RECONNECT
      if (isExplicitClose.current) {
        console.log("Explicit close - cancelling reconnect", reason, code);
        return;
      }

      cleanup();

      if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        // Simple backoff
        const backoffTime = Math.min(1000 * 2 ** retryCount.current, 30000);

        console.log(`${reason}. Reconnecting in ${backoffTime / 1000}s...`);

        retryTimeoutRef.current = window.setTimeout(() => {
          retryTimeoutRef.current = null;
          connect();
        }, backoffTime);
      } else {
        console.error("Max retries reached");
        if (onErrorRef.current)
          onErrorRef.current(new Event("max_retries_exceeded"));
      }
    };

    socket.onclose = (event: CloseEvent) => {
      // 4. STOP THE LOOP: If strict mode or unmount triggered this, ignore it.
      if (isExplicitClose.current) {
        return;
      }

      console.log(`WebSocket closed: ${event.code}`);

      switch (event.code) {
        case 1000: // Normal closure
          if (onCloseRef.current) onCloseRef.current(event);
          break;
        default:
          scheduleReconnect("Connection lost", event.code);
          break;
      }
    };

    socket.onerror = (event: Event) => {
      console.error("WebSocket error observed:", event);
      // Don't act on error, wait for close
    };
  }, [url, token, cleanup]);

  useEffect(() => {
    connect();

    return () => {
      // 5. Cleanup Phase: Set flag to TRUE so onclose doesn't trigger reconnect
      isExplicitClose.current = true;
      cleanup();

      if (socketRef.current) {
        // This closes the socket, which fires 'onclose',
        // but now our 'if (isExplicitClose.current)' check stops the loop.
        socketRef.current.close(1000, "Component unmounting");
        socketRef.current = null;
      }
    };
  }, [connect, cleanup]);

  return {
    send: (data: any) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(data));
        return true;
      }
      return false;
    },
    close: () => {
      isExplicitClose.current = true; // Mark as intentional
      cleanup();
      socketRef.current?.close(1000, "Manual close");
    },
    reconnect: () => {
      isExplicitClose.current = true; // Stop current handlers
      if (socketRef.current) socketRef.current.close();

      // Short timeout to allow close to finish before connecting
      setTimeout(() => {
        isExplicitClose.current = false;
        retryCount.current = 0;
        connect();
      }, 100);
    },
    isConnected: () => socketRef.current?.readyState === WebSocket.OPEN,
  };
}
